import { X } from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';

const UpgradePlanModal = () => {
  const {
    subscription,
    showUpgradeModal,
    setShowUpgradeModal,
    selectedUpgradePlan,
    handleConfirmUpgrade,
    isUpgrading,
    formatDate,
    formatCurrency
  } = useProfile();

  if (!showUpgradeModal || !selectedUpgradePlan) return null;

  const cycleEndDate = subscription?.end_date || subscription?.next_billing_at || subscription?.nextBillingAt;
  const rawDays = cycleEndDate ? Math.max(0, Math.ceil((new Date(cycleEndDate) - new Date()) / (1000 * 60 * 60 * 24))) : 30;
  const daysRemaining = Math.min(30, Math.max(1, rawDays));

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden animate-modal-entry">
        <div className="p-6 pb-4 flex justify-between items-center border-b border-slate-100">
          <div>
            <h2 className="font-bold text-base text-slate-900">
              Upgrade to {selectedUpgradePlan.name} Tier
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Instant mid-cycle plan upgrade</p>
          </div>
          <button onClick={() => setShowUpgradeModal(false)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 cursor-pointer">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 pt-4 space-y-4">
          {/* Plan Comparison */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-3.5 flex justify-between items-center text-xs">
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 block">Current Plan</span>
              <b className="text-xs text-slate-800 font-bold">{subscription?.plan_id?.name || 'Starter'} Tier</b>
              <span className="text-[11px] text-slate-500 block mt-0.5">Ends {formatDate(cycleEndDate)} · <b className="text-teal-700">{daysRemaining} days left</b></span>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-600 block">Upgrading To</span>
              <b className="text-xs text-[#067978] font-bold">{selectedUpgradePlan.name} Tier</b>
              <span className="text-[11px] text-teal-700 block mt-0.5">{selectedUpgradePlan.name} Tier Features</span>
            </div>
          </div>

          {/* Promotional Offer Banner */}
          <div className="rounded-2xl p-4 bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border border-emerald-300/80 shadow-2xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-2.5 py-0.5 rounded-full border border-emerald-300">
                Special Upgrade Perk
              </span>
              <span className="text-[10px] font-semibold text-emerald-700">100% Free Remainder</span>
            </div>
            <p className="text-xs text-slate-800 font-medium leading-relaxed">
              Start using <span className="text-[#067978] font-bold">{selectedUpgradePlan.name} Tier</span> features today for the remaining <span className="font-bold text-slate-900">{daysRemaining} days</span> completely <span className="text-emerald-700 font-bold">FREE</span> (included in your current plan).
            </p>
            <p className="text-[11px] text-slate-600">
              Regular renewal rate of <b>{formatCurrency(selectedUpgradePlan.monthly_price)} / 30 days (+18% GST)</b> will apply starting <b>{formatDate(cycleEndDate)}</b>.
            </p>
          </div>

          {/* Summary Breakdown */}
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/90 text-xs space-y-2.5">
            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">Immediate charge today:</span>
              <b className="text-emerald-700 font-bold bg-emerald-50 px-2.5 py-0.5 rounded border border-emerald-200">
                ₹5.00 (Refundable bank verification)
              </b>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-700 font-bold">First automated debit date:</span>
              <b className="text-[#067978] font-black text-xs bg-teal-50 px-2.5 py-1 rounded-lg border border-teal-200 shadow-2xs">
                {formatDate(cycleEndDate)}
              </b>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">Next cycle charge:</span>
              <b className="text-slate-900 font-bold">{formatCurrency(selectedUpgradePlan.monthly_price)} / 30 days (+18% GST)</b>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-600 rounded-xl cursor-pointer"
            >
              Keep Current Plan
            </button>
            <button
              onClick={handleConfirmUpgrade}
              disabled={isUpgrading}
              className="px-5 py-2.5 bg-[#067978] hover:bg-[#05605f] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-[#067978]/20 flex items-center justify-center cursor-pointer disabled:opacity-60"
            >
              {isUpgrading ? (
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span>Authorize Mandate &amp; Upgrade to {selectedUpgradePlan.name}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePlanModal;
